import type { RouteMeta } from './types';

type OtherGateKey =
  | 'requiresRydEmailDomain'
  | 'requiresListPrice'
  | 'requiredPartnerTypes'
  | 'excludedPartners'
  | 'requiresFleetApis'
  | 'requiresPartnerFeature'
  | 'requiresFleetOpsOrFleetPartner'
  | 'requiresFleetApisOrSubFleet'
  | 'hiddenForSubFleet'
  | 'requiresSubFleet'
  | 'hiddenInProduction';

/**
 * The route meta gates other than roles and user group, each said in words. The access guard
 * (router/index.ts) treats a route as gated when it carries any of these keys or a role or group
 * key; a new gate in the guard must be added here, or the guard will not see it.
 */
const OTHER_GATE_WORDS: Record<OtherGateKey, (meta: RouteMeta) => string> = {
  requiresRydEmailDomain: () => 'ryd e-mail address',
  requiresListPrice: () => 'list-price partner',
  requiredPartnerTypes: (meta) => `partner type ${(meta.requiredPartnerTypes ?? []).join(' or ')}`,
  excludedPartners: (meta) => `not user group ${(meta.excludedPartners ?? []).join(', ')}`,
  requiresFleetApis: () => 'fleet-API partner',
  requiresPartnerFeature: (meta) =>
    `partner feature ${meta.requiresPartnerFeature}` +
    (meta.partnerFeatureOpenForInternalUsers ? ' (ryd users pass)' : ''),
  requiresFleetOpsOrFleetPartner: () => 'fleet-API or fleet partner',
  requiresFleetApisOrSubFleet: () => 'fleet-API partner or sub-fleet',
  hiddenForSubFleet: () => 'not a sub-fleet',
  requiresSubFleet: () => 'sub-fleets only',
  hiddenInProduction: () => 'not on production',
};

const OTHER_GATE_KEYS = Object.keys(OTHER_GATE_WORDS) as OtherGateKey[];

export const ACCESS_GATE_KEYS: (keyof RouteMeta)[] = [
  'requiredRoles',
  'requiredAnyRole',
  'requiredUserGroup',
  ...OTHER_GATE_KEYS,
];

interface RecordWithMeta {
  meta?: unknown;
}

const metaOf = (record: RecordWithMeta) => (record.meta ?? {}) as RouteMeta;

export function hasAccessGate(record: RecordWithMeta): boolean {
  const meta = metaOf(record);
  return ACCESS_GATE_KEYS.some((key) => Boolean(meta[key]));
}

/**
 * The matched records the guard checks: the first one with any gate (all its gates), plus every
 * other record that opts in with `strictRoles` (their roles only, CF-63).
 */
export function accessRecords<T extends RecordWithMeta>(
  matched: readonly T[],
): { primary: T | undefined; roleRecords: T[] } {
  const primary = matched.find(hasAccessGate);
  if (!primary) return { primary, roleRecords: [] };
  const strict = matched.filter((record) => record !== primary && metaOf(record).strictRoles);
  return { primary, roleRecords: [primary, ...strict] };
}

export interface RouteAccessSummary {
  /** The user's group must be one of these; empty means any group. */
  userGroups: string[];
  /** The user needs every one of these roles. */
  allRoles: string[];
  /** For each list, the user needs at least one of its roles. */
  anyRoles: string[][];
  /** The remaining gates, in words. */
  otherGates: string[];
}

/** What the guard requires to open a route, from its matched records (`router.resolve(path).matched`). */
export function describeRouteAccess(matched: readonly RecordWithMeta[]): RouteAccessSummary {
  const { primary, roleRecords } = accessRecords(matched);
  const summary: RouteAccessSummary = {
    userGroups: [],
    allRoles: [],
    anyRoles: [],
    otherGates: [],
  };
  if (!primary) return summary;
  const meta = metaOf(primary);
  summary.userGroups = [...(meta.requiredUserGroup ?? [])];
  for (const record of roleRecords) {
    const recordMeta = metaOf(record);
    for (const role of recordMeta.requiredRoles ?? []) {
      if (!summary.allRoles.includes(role)) summary.allRoles.push(role);
    }
    if (recordMeta.requiredAnyRole?.length) summary.anyRoles.push([...recordMeta.requiredAnyRole]);
  }
  summary.otherGates = OTHER_GATE_KEYS.filter((key) => Boolean(meta[key])).map((key) =>
    OTHER_GATE_WORDS[key](meta),
  );
  return summary;
}
